import React from 'react'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import AddButton from '@mui/icons-material/AddCircleOutlined'
import MoreVertRounded from '@mui/icons-material/MoreVertRounded'
import Button from '../../components/Button/Button'
import { useUiStore } from '../../stores/uiStore'
import { useAuthStore } from '../../stores/authStore'
import AddParticipantModal, { ParticipantFormValues } from '../Dashboard/PlanActivity/components/AddParticipantModal'
import CircularProgress from '@mui/material/CircularProgress'
import ScoreboardParticipantsList from './ScoreboardParticipantsList'
import AddResultModal from './AddResultModal'
import { ScoreboardParticipant } from '../../utils/types'
import { addSpacing } from '../../utils/hoc'
import Text from '../../components/Text'
import { useNavigate } from 'react-router-dom'

type ScoreboardHeaderProps = {
  onParticipantAdded: (participants: ParticipantFormValues[]) => void
  isAddingParticipant: boolean
  participants: ScoreboardParticipant[]
  deleteParticipant: (participantId: number) => void
  hasParticipants: boolean
  hasWriteAccess: boolean
  onDeleteScoreboard: () => void
  creatorUsername?: string
  onCreateScoreboardResult: (label: string) => void
  isCreatingScoreboardResult?: boolean
  defaultNewResultLabel?: string
}

const ScoreboardHeader = ({
  onParticipantAdded,
  isAddingParticipant,
  participants,
  deleteParticipant,
  hasParticipants,
  hasWriteAccess,
  onDeleteScoreboard,
  creatorUsername,
  onCreateScoreboardResult,
  isCreatingScoreboardResult,
  defaultNewResultLabel,
}: ScoreboardHeaderProps) => {
  return (
    <div>
      <ActionRow
        defaultNewResultLabel={defaultNewResultLabel}
        mb={2}
        onParticipantAdded={onParticipantAdded}
        isAddingParticipant={isAddingParticipant}
        hasWriteAccess={hasWriteAccess}
        onDeleteScoreboard={onDeleteScoreboard}
        creatorUsername={creatorUsername}
        onCreateScoreboardResult={onCreateScoreboardResult}
        isCreatingScoreboardResult={isCreatingScoreboardResult}
      />
      <ScoreboardParticipantsList
        participants={participants}
        deleteParticipant={deleteParticipant}
        hasParticipants={hasParticipants}
        creatorUsername={creatorUsername}
        hasWriteAccess={hasWriteAccess}
      />
    </div>
  )
}

export default ScoreboardHeader

type ActionButtonsProps = {
  onParticipantAdded: (participants: ParticipantFormValues[]) => void
  isAddingParticipant: boolean
  hasWriteAccess: boolean
  onDeleteScoreboard: () => void
  onCreateScoreboardResult: (label: string) => void
  isCreatingScoreboardResult?: boolean
  creatorUsername?: string
  style?: React.CSSProperties
  defaultNewResultLabel?: string
}

type ScoreboardActionsMenuItem = {
  id: string
  label: string
  visible: boolean
  destructive?: boolean
  icon?: React.ReactNode
  onSelect: () => void
}

const ActionRowBase = ({
  onParticipantAdded,
  isAddingParticipant,
  hasWriteAccess,
  onDeleteScoreboard,
  onCreateScoreboardResult,
  defaultNewResultLabel,
  isCreatingScoreboardResult,
  creatorUsername,
  style,
}: ActionButtonsProps) => {
  const navigate = useNavigate()
  const { showDialog } = useUiStore()
  const { currentUser } = useAuthStore()
  const canDeleteScoreboard = !!creatorUsername && currentUser?.username === creatorUsername
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null)
  const menuOpen = Boolean(menuAnchor)

  const closeMenu = () => setMenuAnchor(null)

  const openAddResultDialog = () => {
    closeMenu()
    showDialog({
      dialogBody: (
        <AddResultModal 
          defaultLabel={defaultNewResultLabel}
          onCreateResult={onCreateScoreboardResult} 
          isLoading={isCreatingScoreboardResult} 
        />
      ),
    })
  }

  const handleDeleteScoreboard = () => {
    closeMenu()
    onDeleteScoreboard()
  }

  const handleShowTable = () => {
    const currentPath = window.location.pathname + '/table'
    navigate(currentPath)
    closeMenu()
  }

  const scoreboardActionsMenuItems: ScoreboardActionsMenuItem[] = [
    {
      id: 'add-new-result',
      label: 'Add new result',
      visible: hasWriteAccess,
      onSelect: openAddResultDialog,
    },
    {
      id: 'delete-scoreboard',
      label: 'Delete Scoreboard',
      visible: canDeleteScoreboard,
      onSelect: handleDeleteScoreboard,
    },
    {
      id: 'show-table',
      label: 'Show table',
      visible: true,
      onSelect: handleShowTable,
    },
  ]

  const visibleMenuItems = scoreboardActionsMenuItems.filter((item) => item.visible)

  return (
    <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} style={style}>
      {hasWriteAccess ? (
        <Button
          size="small"
          startIcon={isAddingParticipant ? <CircularProgress size={18} /> : <AddButton />}
          variant="contained"
          onClick={() =>
            showDialog({
              dialogBody: <AddParticipantModal storeParticipant={onParticipantAdded} />,
            })
          }
        >
          Add Participants
        </Button>
      ) : (
        <span />
      )}
      <Box display="flex" alignItems="center" sx={{ marginLeft: 'auto' }}>
        {visibleMenuItems.length > 0 && (
        <IconButton
          aria-label="Scoreboard actions"
          aria-controls={menuOpen ? 'scoreboard-actions-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={menuOpen ? 'true' : undefined}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          size="small"
        >
          <MoreVertRounded fontSize="large" />
        </IconButton>
        )}
        <Menu id="scoreboard-actions-menu" anchorEl={menuAnchor} open={menuOpen} onClose={closeMenu} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
          {visibleMenuItems.map((item) => (
            <MenuItem key={item.id} onClick={item.onSelect}>
              {item.icon != null ? (
                <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
              ) : null}
              <ListItemText sx={item.destructive ? { color: 'error.main' } : undefined}>
                <Text weight="semibold">{item.label}</Text>
              </ListItemText>
            </MenuItem>
          ))}
        </Menu>
      </Box>
    </Box>
  )
}

const ActionRow = addSpacing<ActionButtonsProps>(ActionRowBase)
